/**
 * لاگِ روی دستگاه — یه دفترِ کوچیکِ سطرها، کادری که نشونشون می‌ده، و دکمه‌ای
 * که می‌فرستدشون بالا.
 *
 * از دلِ `swipeTrace` در اومد. اون فقط ژستِ سواپ رو می‌نوشت، و اولین باری که
 * باگِ دیگه‌ای روی بیلدِ منتشرشده پیش اومد (پنجره‌های نوارِ کشف که باز
 * نمی‌شدن) هیچی برای فرستادن نبود. ابزارِ تشخیصی که فقط یه باگِ مشخص رو
 * بلده، دقیقاً همون باگِ بعدی رو نمی‌گیره.
 *
 * پس حالا این‌جا **عمومی**ه: هر جای اپ می‌تونه `trace.log('...')` بزنه، هر
 * خطایی که از `reportError` رد شه خودش این‌جا هم می‌شینه، و `console.warn` و
 * `console.error` هم گرفته می‌شن. کادر روی همه‌ی صفحه‌هاست (نگاه کن به
 * ui/TraceOverlay و app/_layout) نه فقط سربرگ‌های پایین.
 *
 * خوندنِ چیزی که فرستاده شده:
 *
 *   select created_at, message, detail->>'lines' from client_errors
 *   where where_at = 'trace' order by created_at desc;
 *
 * خاموش که باشه هیچ‌کدوم از این‌ها کاری نمی‌کنن: `log` توی خطِ اول برمی‌گرده،
 * پوششِ کنسول یه مقایسه‌ی خالیه، و هیچ درخواستی به شبکه نمی‌ره.
 *
 * و خاموشْ حالتِ پیش‌فرضِ همه است. کلیدش توی ترجیح‌های همین دستگاهه
 * (`prefs.traceLog`) و تنها نویسنده‌ش `setTrace` است، که فقط از تنظیماتِ
 * `TRACE_OWNER` در دسترسه.
 */
import { supabase } from './supabase.ts';
import { prefs, setPref } from './prefs.ts';
import { platformInfo } from '../platform.ts';

/**
 * تنها حسابی که این کلید براش معنی داره.
 *
 * چرا نه `isAdmin`: این ابزارِ بازبینی نیست، داربستِ دیباگه — کادری که وسطِ
 * صفحه می‌شینه و لاگ رو می‌فرسته بالا. هر ادمینِ تازه‌ای که اضافه شه نباید
 * ناگهان یه گزینه‌ی نامفهوم توی تنظیماتش سبز شه.
 */
export const TRACE_OWNER = 'parvij';

/** آیا لاگ روشنه. هر ورودیِ این فایل اول همین رو می‌پرسه. */
export const traceOn = (): boolean => prefs.traceLog;

/** بیشتر از این سطر نگه نمی‌داریم؛ روی صفحه هم جا نمی‌شه. */
const KEEP = 14;
/**
 * بعد از یه سطرِ مشکوک این‌قدر صبر می‌کنیم و بعد می‌فرستیم — تا چند تلاشِ
 * پشتِ همِ کاربر (که معمولاً وقتی چیزی کار نمی‌کنه همین کار رو می‌کنه) توی
 * یه سطرِ واحد کنارِ هم دیده بشن.
 */
const QUIET = 6_000;
/** و بین دو تا فرستادن دستِ‌کم این‌قدر فاصله. */
const COOLDOWN = 30_000;
/** سقفِ هر بار بالا اومدنِ اپ، تا یه بعدازظهر کار کردن جدول رو پر نکنه. */
const MAX_SENDS = 40;

/** شناسه‌ی کوتاهِ این بار بالا اومدنِ اپ، تا سطرهای یه نشست به هم وصل شن. */
const session = Math.random().toString(36).slice(2, 6).toUpperCase();

let lines: string[] = [];
const listeners = new Set<() => void>();
const publish = () => { listeners.forEach((l) => l()); };

let sends = 0;
let lastSend = 0;
let failed = '';
let pending = false;
let sendTimer: ReturnType<typeof setTimeout> | null = null;

/** فرستادنِ سطرهای فعلی. `reason` توی `message` می‌شینه: `auto`/`manual`/`bug`. */
function upload(reason: string): void {
  // آخرین درِ خروج به شبکه. تایمری که وسطِ خاموش شدن در راه بوده هم از این
  // در رد می‌شه، پس همین‌جا بسته می‌شه.
  if (!traceOn()) return;
  if (sends >= MAX_SENDS || lines.length === 0) return;
  pending = false;
  lastSend = Date.now();
  sends += 1;
  publish();
  /**
   * عمداً بدونِ `getUser`: اون یه رفت‌وبرگشتِ شبکه‌ست و اگه نشستِ کاربر
   * منقضی شده باشه همون‌جا می‌ایسته و لاگ هیچ‌وقت نمی‌رسه. سیاستِ `ce_ins`
   * سطرِ بی‌صاحب رو قبول می‌کنه، و ما هم از این سطرها چیزی جز خودِ لاگ
   * نمی‌خوایم.
   */
  const p = platformInfo();
  void supabase.from('client_errors').insert({
    user_id: null,
    where_at: 'trace',
    message: `${reason} · ${session}`,
    detail: { lines: lines.join('\n') },
    app_version: p.appVersion,
    platform: `${p.os} ${p.osVersion}`,
  }).then(({ error }) => {
    failed = error ? error.message.slice(0, 40) : '';
    publish();
  });
}

/** هنوز توی فاصله‌ی اجباری‌ایم؛ همین‌قدر که آزاد شد بفرست. */
function scheduleRetry(): void {
  if (sendTimer) return;
  sendTimer = setTimeout(() => {
    sendTimer = null;
    if (pending) upload('auto');
  }, Math.max(0, COOLDOWN - (Date.now() - lastSend)));
}

/** «این ارزشِ فرستادن داره، ولی عجله نداره» — چند سطرِ بعدی رو هم بگیر و برو. */
function scheduleSend(): void {
  pending = true;
  if (sendTimer) return;
  sendTimer = setTimeout(() => {
    sendTimer = null;
    if (!pending) return;
    if (Date.now() - lastSend < COOLDOWN) { scheduleRetry(); return; }
    upload('auto');
  }, QUIET);
}

export interface LogOptions {
  /** همین حالا بفرست — برای چیزهایی که خودشون خودِ باگ‌ان. */
  urgent?: boolean;
  /** ارزشِ فرستادن داره ولی عجله نداره؛ با سطرهای بعدی یه‌جا می‌ره. */
  soon?: boolean;
}

export const trace = {
  /**
   * یه سطرِ تازه بالای دفتر.
   *
   * `console.log` هم صدا زده می‌شه (نه warn/error - وگرنه پوششِ کنسولِ پایین
   * همین سطر رو دوباره می‌نویسه و حلقه می‌شه).
   */
  log(text: string, opts: LogOptions = {}): void {
    if (!traceOn()) return;
    lines = [text, ...lines].slice(0, KEEP);
    console.log('[trace]', text);
    if (opts.urgent) { pending = true; upload('bug'); } else if (opts.soon) scheduleSend();
    publish();
  },

  /** دکمه‌ی روی کادر: همین حالا بفرست، بی‌توجه به فاصله‌ی اجباری. */
  send(): void {
    upload('manual');
  },

  /** خالی کردنِ دفتر — دکمه‌ی «پاک» روی کادر. */
  clear(): void {
    lines = [];
    publish();
  },

  subscribe(fn: () => void): () => void {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  },

  lines(): string[] {
    return lines;
  },

  /** برای نوارِ بالای کادر: چند تا رفت، و اگه نرفت چرا. */
  status(): string {
    return `${session} ↑${sends}${pending ? '…' : ''}${failed ? ` ✘${failed}` : ''}`;
  },
};

/** خاموش که شد، هیچی از دورِ قبل نباید بمونه: نه تایمر، نه سطرِ روی صفحه. */
function reset(): void {
  if (sendTimer) { clearTimeout(sendTimer); sendTimer = null; }
  pending = false;
  lines = [];
  publish();
}

/**
 * روشن/خاموش کردنِ لاگ. تنها نویسنده‌ی `prefs.traceLog` همینه.
 *
 * `publish` در هر دو حالت صدا زده می‌شه، چون کادرِ روی صفحه از همین شنونده‌ها
 * خبردار می‌شه و نه از رندرِ دوباره‌ی درختِ ناوبری (که React Navigation
 * مموایزش می‌کنه و ممکنه اصلاً اتفاق نیفته).
 */
export function setTrace(on: boolean): void {
  void setPref('traceLog', on);
  if (on) publish(); else reset();
}

/**
 * هر بار که فهمیدیم الان کی وارد شده.
 *
 * کلید روی همین دستگاه ذخیره می‌شه، نه روی حساب — پس اگه گوشی دستِ حسابِ
 * دیگه‌ای بیفته باید خودش خاموش شه. عمداً هیچ‌وقت **روشن** نمی‌کنه: انتخابِ
 * روشن کردن فقط از تنظیمات میاد، وگرنه خاموش کردنِ کاربر با ورودِ بعدیش
 * برمی‌گشت.
 */
export function syncTraceOwner(username?: string | null): void {
  if (prefs.traceLog && username !== TRACE_OWNER) setTrace(false);
}

/**
 * `console.warn` و `console.error` هم توی همین دفتر بشینن.
 *
 * چیزی که اپ به کنسول می‌گه، روی بیلدِ منتشرشده هیچ‌جا دیده نمی‌شه — نه
 * Metroیی هست نه لاگ‌کتی. این یعنی نصفِ چیزی که موقعِ دیباگ لازمه، دقیقاً
 * همون‌جایی که لازمه در دسترس نیست. یه بار موقعِ بالا اومدنِ اپ صدا زده
 * می‌شه (app/_layout).
 */
export function installTraceConsole(): void {
  const c = console as unknown as Record<string, (...args: unknown[]) => void>;
  for (const level of ['warn', 'error'] as const) {
    const original = c[level];
    c[level] = (...args: unknown[]) => {
      original(...args);
      // خاموش که باشه هیچ کاری نمی‌کنه؛ و `log` خودش هم فقط `console.log`
      // می‌زنه، پس این‌جا هیچ حلقه‌ای بسته نمی‌شه.
      if (!traceOn()) return;
      const text = args.map((a) => (typeof a === 'string' ? a : String(a))).join(' ');
      // خطاهای خودِ اپ از `reportError` رد می‌شن و اون سطرِ خواناترِ خودشو
      // می‌نویسه؛ بدونِ این هر خطا دو بار توی کادر می‌نشست.
      if (text.startsWith('[goout:')) return;
      trace.log(`${level === 'warn' ? '⚠' : '✘'} ${text.slice(0, 160)}`);
    };
  }
}
